import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { LoginServiceError, LoginServiceErrorCode } from "./LoginServiceErrors";
import { TermsOfUse } from "../components/pages/TermOfUsePage";
import { isAllowedHostname } from "../url-util";

let loginService: LoginService | undefined;

const checkLoginService = (loginServiceBaseUrl: string) => {
  const loginServiceURL = new URL(loginServiceBaseUrl);
  if (isAllowedHostname(loginServiceURL.hostname)) {
    return true;
  } else {
    throw new Error("Wrong login service url was passed");
  }
};
export function initLoginService(loginServiceBaseUrl: string) {
  loginService = new LoginService(
    loginServiceBaseUrl && checkLoginService(loginServiceBaseUrl)
      ? loginServiceBaseUrl
      : LOGIN_SERVICE_URL /* comes from local.js */
  );
}

export function getLoginService() {
  if (!loginService) {
    throw new Error(
      "Login Service is not instantiated. Call `initLoginService` function first"
    );
  }

  return loginService;
}

interface ILoginResponse {
  accessToken: string;
}

interface IEnsureAccessTokenExistsResponse {
  exists: boolean;
}

type TermsOfUseResponse = { termsOfUse: TermsOfUse } | null;

export class LoginService {
  loginService: AxiosInstance;

  constructor(loginServiceBaseUrl: string) {
    this.loginService = axios.create({
      baseURL: loginServiceBaseUrl
    });
  }

  login(email: string, password: string): Promise<ILoginResponse> {
    return this.loginService
      .post(
        LOGIN_SERVICE_LOGIN_ENDPOINT,
        {
          email,
          password
        },
        {
          withCredentials: true
        }
      )
      .catch(this.handleLoginError)
      .then((response: AxiosResponse<ILoginResponse>) => response.data);
  }

  getLatestTermsOfUse(): Promise<TermsOfUseResponse> {
    return this.loginService
      .get(LOGIN_SERVICE_TERMS_OF_USE_LATEST)
      .catch(this.handleUnknownErrors)
      .then((response: AxiosResponse<TermsOfUseResponse>) => response.data);
  }

  agreeTermsOfUse(touDate: string) {
    return this.loginService
      .put(
        LOGIN_SERVICE_TERMS_OF_USE_AGREE,
        {
          dateAdded: touDate,
          agreed: true
        },
        {
          withCredentials: true
        }
      )
      .catch(this.handleAgreeTermsOfUseError);
  }

  logout() {
    return this.loginService.post(
      LOGIN_SERVICE_LOGOUT,
      {},
      { withCredentials: true }
    );
  }

  ensureUserLoggedIn() {
    return this.loginService.post(
      LOGIN_SERVICE_ENSURE_USER,
      {},
      { withCredentials: true }
    );
  }

  private handleLoginError(errorResponse: AxiosError): LoginServiceError {
    if (errorResponse.response && errorResponse.response.status === 401) {
      throw new LoginServiceError(LoginServiceErrorCode.INVALID_CREDENTIALS);
    }
    if (
      errorResponse.response &&
      errorResponse.response.status === 403 &&
      (errorResponse.response.data as any)?.code === "NO_TERMS_OF_USE_AGREEMENT"
    ) {
      throw new LoginServiceError(LoginServiceErrorCode.TOU_NOT_AGREED);
    }
    if (errorResponse.response && errorResponse.response.status === 429) {
      const retryAfter = (errorResponse.response.data as any).retryAfter;
      throw new LoginServiceError(LoginServiceErrorCode.TOO_MANY_ATTEMPTS, {
        retryAfter
      });
    }

    throw this.handleUnknownErrors(errorResponse);
  }

  private handleAgreeTermsOfUseError(
    errorResponse: AxiosError
  ): LoginServiceError {
    const { response } = errorResponse;
    if (response && response.status === 401) {
      throw new LoginServiceError(LoginServiceErrorCode.UNAUTHORIZED);
    }
    if (
      response &&
      response.status === 403 &&
      (response.data as any)?.code === "TOU_DATE_MISMATCH"
    ) {
      throw new LoginServiceError(LoginServiceErrorCode.TOU_DATE_MISMATCH);
    }
    throw this.handleUnknownErrors(errorResponse);
  }

  requestPasswordRecovery(email: string, refererUrl: string): Promise<void> {
    return this.loginService
      .post(
        LOGIN_SERVICE_REQUEST_RECOVERY_PASSWORD_ENDPOINT,
        {
          email,
          refererUrl: refererUrl,
          loginServiceBaseUrl: encodeURIComponent(
            this.loginService.defaults.baseURL as string
          )
        },
        {
          withCredentials: true
        }
      )
      .catch(this.handlePasswordRecoveryRequestedError)
      .then(() => {});
  }
  handlePasswordRecoveryRequestedError(errorResponse: AxiosError) {
    if (errorResponse) {
      const { code } = errorResponse;
      if (code === "EMAIL_IS_EMPTY") {
        throw new LoginServiceError(LoginServiceErrorCode.EMAIL_IS_EMPTY);
      }
    }
    this.handleUnknownErrors(errorResponse);
  }
  ensureAccessTokenExists(accessToken: string) {
    return this.loginService
      .post(LOGIN_SERVICE_ENSURE_ACCESS_TOKEN_EXISTS, {
        accessToken
      })
      .catch(this.handlePasswordSetError)
      .then(
        (response: AxiosResponse<IEnsureAccessTokenExistsResponse>) =>
          response.data
      );
  }
  setNewPassword(newPassword: string, passwordToken: string): Promise<void> {
    return this.loginService
      .post(
        LOGIN_SERVICE_NEW_PASSWORD_ENDPOINT,
        {
          token: passwordToken,
          password: newPassword
        },
        {
          withCredentials: true
        }
      )
      .catch(this.handlePasswordSetError)
      .then(() => {});
  }
  handlePasswordSetError(errorResponse: AxiosError) {
    if (errorResponse) {
      const { code } = errorResponse;
      if (code === "PASSWORD_TOO_SHORT") {
        throw new LoginServiceError(LoginServiceErrorCode.WEAK_PASSWORD);
      }
    }
    this.handleUnknownErrors(errorResponse);
  }
  handleUnknownErrors(errorResponse: AxiosError): LoginServiceError {
    if (errorResponse.response) {
      throw new LoginServiceError(LoginServiceErrorCode.UNKNOWN_RESPONSE);
    }
    throw new LoginServiceError(LoginServiceErrorCode.REQUEST_FAILED);
  }
}
